import type { ElementType, ReactElement, ReactNode } from "react";
import {
  Menu,
  type MenuItemProps,
  type MenuRootProps,
} from "@base-ui/react/menu";
import styles from "./DropdownMenu.module.css";
import Button from "./Button.tsx";
import IconButton from "./IconButton.tsx";
// @ts-expect-error pure js
import { classnames } from "../../utils";
import Text from "./Text.tsx";
import useDialogPortalContainer from "../../hooks/use-dialog-portal-container.ts";
import useCanWrite from "../../hooks/use-can-write";
import { READ_ONLY_MESSAGE } from "../../utils/permissions";

interface DropdownMenuItemProps extends MenuItemProps {
  variant?: "neutral" | "success" | "danger" | "warning";
  icon?: ElementType;
  label: string;
  requiresWrite?: boolean;
  profileId?: number | null;
}

function DropdownMenuItem({
  className,
  variant = "neutral",
  icon: Icon,
  label,
  disabled,
  requiresWrite,
  profileId,
  ...props
}: DropdownMenuItemProps) {
  const writable = useCanWrite(profileId);
  const blocked = requiresWrite === true && !writable;

  return (
    <Menu.Item
      className={classnames(styles.item, styles[variant], className)}
      disabled={blocked || disabled}
      title={blocked ? READ_ONLY_MESSAGE : undefined}
      render={
        <Text as="div" color={variant}>
          {Icon && <Icon size={16} />}
          {label}
        </Text>
      }
      {...props}
    />
  );
}

type DropdownMenuProps = MenuRootProps & {
  children: ReactNode;
  align?: "start" | "center" | "end";
  containerSelector?: string;
  className?: string;
} & (
    | {
        icon: ElementType;
        label: string;
        /** Affiche le libellé à côté de l'icône (masqué en mobile). */
        showLabel?: boolean;
        trigger?: never;
        nativeButton?: never;
      }
    | {
        trigger: ReactElement;
        icon?: never;
        label?: never;
        showLabel?: never;
        nativeButton?: boolean;
      }
  );

export default function DropdownMenu({
  icon,
  label,
  showLabel,
  trigger,
  align,
  children,
  containerSelector,
  nativeButton,
  className,
  ...props
}: DropdownMenuProps) {
  const { container, triggerRef } = useDialogPortalContainer(containerSelector);

  const defaultTrigger = showLabel ? (
    <Button variant="secondary" icon={icon!} aria-label={label}>
      <span className={styles.triggerLabel}>{label}</span>
    </Button>
  ) : (
    <IconButton icon={icon!} label={label!} />
  );

  return (
    <Menu.Root {...props}>
      <div onClick={(e) => e.stopPropagation()} className={className}>
        <Menu.Trigger
          ref={triggerRef}
          nativeButton={nativeButton}
          render={trigger ?? defaultTrigger}
        />
        <Menu.Portal container={container}>
          <Menu.Positioner sideOffset={4} align={align}>
            <Menu.Popup className={styles.popup}>{children}</Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </div>
    </Menu.Root>
  );
}

DropdownMenu.Item = DropdownMenuItem;
